import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { useEffect } from "react";
import { stopStyle } from "@/lib/status";

function stopIcon(seq, color, numbered) {
  return L.divIcon({
    className: "",
    html: `<div class="stop-pin ${numbered ? "numbered" : ""}" style="background:${color}"><span>${seq}</span></div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -28],
  });
}

const garageIcon = L.divIcon({
  className: "",
  html: `<div class="bus-pin">G</div>`,
  iconSize: [34, 34], iconAnchor: [17, 17],
});

const schoolIcon = L.divIcon({
  className: "",
  html: `<div class="bus-pin" style="background:#7C3AED;border-radius:8px">É</div>`,
  iconSize: [34, 34], iconAnchor: [17, 17],
});

const driverIcon = L.divIcon({
  className: "",
  html: `<div style="width:20px;height:20px;border-radius:50%;background:#2563EB;border:3px solid #fff;box-shadow:0 0 0 6px rgba(37,99,235,.3)"></div>`,
  iconSize: [20, 20], iconAnchor: [10, 10],
});

function busIcon(fleet) {
  return L.divIcon({
    className: "",
    html: `<div class="bus-pin" style="border-radius:50%">${(fleet || "").slice(-2)}</div>`,
    iconSize: [34, 34], iconAnchor: [17, 17],
  });
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points && points.length > 0) {
      const b = L.latLngBounds(points);
      map.fitBounds(b, { padding: [40, 40], maxZoom: 14 });
    }
  }, [JSON.stringify(points)]); // eslint-disable-line
  return null;
}

export default function MapView({
  stops = [], geometry = [], garage, compareGeometry, compareColor = "#94A3B8",
  polylineColor = "#3B82F6", vehicles = [], showNumbers = true, center = [45.4, -73.28],
  school, driverPosition,
}) {
  const stopPoints = stops.filter((s) => s.lat).map((s) => [s.lat, s.lng]);
  const allPoints = [...stopPoints, ...(garage ? [[garage.lat, garage.lng]] : []),
    ...(school?.lat ? [[school.lat, school.lng]] : [])];

  return (
    <MapContainer center={center} zoom={12} scrollWheelZoom style={{ height: "100%", width: "100%" }} data-testid="map-container">
      <TileLayer
        attribution='&copy; OpenStreetMap'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <FitBounds points={allPoints.length ? allPoints : geometry} />

      {compareGeometry && compareGeometry.length > 1 && (
        <Polyline positions={compareGeometry} pathOptions={{ color: compareColor, weight: 4, opacity: 0.5, dashArray: "6 8" }} />
      )}
      {geometry && geometry.length > 1 && (
        <Polyline positions={geometry} pathOptions={{ color: polylineColor, weight: 5, opacity: 0.9 }} />
      )}

      {garage && (
        <Marker position={[garage.lat, garage.lng]} icon={garageIcon}>
          <Popup>{garage.name || "Garage"}</Popup>
        </Marker>
      )}

      {school?.lat && (
        <Marker position={[school.lat, school.lng]} icon={schoolIcon}>
          <Popup>{school.name || "École"} — destination finale</Popup>
        </Marker>
      )}

      {driverPosition?.lat && (
        <Marker position={[driverPosition.lat, driverPosition.lng]} icon={driverIcon}>
          <Popup>Votre position</Popup>
        </Marker>
      )}

      {stops.filter((s) => s.lat).map((s, i) => {
        const st = stopStyle(s);
        return (
          <Marker key={s.id || i} position={[s.lat, s.lng]}
            icon={stopIcon(showNumbers ? i + 1 : "", st.color, s.numbered_road)}>
            <Popup>
              <div style={{ minWidth: 180 }}>
                <div style={{ fontWeight: 700 }}>#{i + 1} — {s.address}</div>
                <div style={{ color: "#64748b" }}>{s.city}</div>
                {s.road_number && <div>Route {s.road_number} · {s.speed_limit} km/h</div>}
                <div>Côté: {s.stop_side || "?"} · Requis: {s.required_stop_side || "—"}</div>
                <div>Direction bus: {s.heading_cardinal || "—"}</div>
                <div style={{ fontWeight: 600, color: st.color }}>{st.label}</div>
              </div>
            </Popup>
          </Marker>
        );
      })}

      {vehicles.filter((v) => v.position).map((v) => (
        <Marker key={v.assignment_id} position={[v.position.lat, v.position.lng]} icon={busIcon(v.bus_fleet)}>
          <Popup>
            <div style={{ minWidth: 170 }}>
              <div style={{ fontWeight: 700 }}>Autobus {v.bus_fleet} · Route {v.route_number}</div>
              <div>{v.driver_name}</div>
              <div>Prochain: {v.next_stop?.address || "—"}</div>
              <div>Progression: {v.progress}</div>
              <div>Retard: {v.delay_min || 0} min</div>
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}
